import { IPathToPageIdMap } from '@move-frontend/contentful-routes';
import { useCommonData } from '../../../../common-data/useCommonData';
import {
	CardSectionDisplay,
	ICardSectionEntry,
} from '../../../../contentful/types/ICardSection';
import { Section, SectionHeader } from './Section';
import { ICardItemEntry } from '../../../../contentful/types/ICardItem';
import { getListedEntryKey } from '../../../../utils/getListedEntryKey';
import Card from '../../../../components/common/cards/Card/Card';
import CardsContainer from '../../../../components/common/cards/CardsContainer';
import { getUrlForLink } from '../../../../utils/getUrlForLink';
import FeatureEntrypoint from '../../../../components/common/cards/FeatureEntrypoint';
import { getFeatureImageSource } from '../../../../utils/getFeatureImageSource';
import { getCardImageSource } from '../../../../utils/getCardImageSource';
import FlowyCard from '../../../../components/common/cards/FlowyCard/FlowyCard';

export interface ICardSectionProps {
	section: ICardSectionEntry;
}

const renderCardItem = (
	item: ICardItemEntry,
	index: number,
	displayAs: CardSectionDisplay,
	pathToPageIdMap: IPathToPageIdMap,
) => {
	const {
		fields: { link, title, image, categories },
	} = item;

	const key = getListedEntryKey(item, index);
	const linkTitle = item.fields.link?.fields.title;
	const linkUrl = getUrlForLink(link, pathToPageIdMap);
	let isClickable = false;

	if (displayAs === CardSectionDisplay.Feature) {
		return (
			<FeatureEntrypoint
				key={key}
				image={getFeatureImageSource(image)}
				label={title}
				href={linkUrl}
			/>
		);
	}

	if (displayAs === CardSectionDisplay.Flowy) {
		return (
			<FlowyCard
				imageSource={getCardImageSource(image)}
				title={item.fields.title}
				body={item.fields.body}
			/>
		);
	}

	if (displayAs === CardSectionDisplay.Clickable) {
		isClickable = linkUrl !== undefined;
	}

	return (
		<Card
			key={key}
			title={item.fields.title}
			imageSource={getCardImageSource(image)}
			linkHref={isClickable ? linkUrl : undefined}
			linkTitle={linkTitle}
			body={item.fields.body}
			fullyClickable={isClickable}
			categories={categories}
		/>
	);
};

export function CardSection({ section }: ICardSectionProps) {
	const { pathToPageIdMap } = useCommonData();

	const getColumnCounts = (displayAs: CardSectionDisplay) => {
		if (displayAs === CardSectionDisplay.Flowy) {
			return { columns: 4 };
		}
		return { columns: 3 };
	};

	const { columns } = getColumnCounts(section.fields.displayAs);
	const flowy = section.fields.displayAs === CardSectionDisplay.Flowy;

	return (
		<Section>
			{section.fields.title && (
				<SectionHeader>{section.fields.title}</SectionHeader>
			)}
			<CardsContainer columns={columns} flowy={flowy}>
				{section.fields.items &&
					section.fields.items.map((item: ICardItemEntry, index: number) =>
						renderCardItem(
							item,
							index,
							section.fields.displayAs,
							pathToPageIdMap,
						),
					)}
			</CardsContainer>
		</Section>
	);
}
